import React from 'react';
import { makeStyles, FormControl, InputLabel, Select, MenuItem, Box } from '~/lib/bn-material-ui';
import { BnSearchField } from '~/common';
import { DEFAULT_MATERIAL_COLOR } from './material-colors';

const useStyles = makeStyles(theme => ({
  container: {
    position: 'absolute',
    top: '-5rem',
    right: theme.spacing(1),
    zIndex: 100,
    padding: theme.spacing(1),
    minWidth: '200px',
    userSelect: 'none',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
  },
  filterRow: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  formControl: {
    minWidth: '11rem',
    flex: 1,
  },
  searchField: {
    marginTop: '0.5rem',
  },
  materialItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  colorSymbol: {
    fontSize: '12px',
    lineHeight: 1,
  },
}));

// These will be populated dynamically from the actual bin data
export const BinTypes = {};
export const Materials = {};

export const LevelRanges = {
  ZERO_TO_TWENTY_FIVE: '0-25',
  TWENTY_FIVE_TO_FIFTY: '25-50',
  FIFTY_TO_SEVENTY_FIVE: '50-75',
  SEVENTY_FIVE_TO_HUNDRED: '75-100',
};

/**
 * Component for filtering bins on the inventory map
 * @param {Object} props - Component props
 * @param {string} props.selectedBinType - Currently selected bin type
 * @param {string} props.selectedMaterial - Currently selected material
 * @param {string} props.selectedLevelRange - Currently selected level range
 * @param {string} props.searchQuery - Current search query
 * @param {Array} props.availableBinTypes - Available bin types from the data
 * @param {Array} props.availableMaterials - Available materials from the data
 * @param {Object} props.materialColorMap - Map of material names to colors
 * @param {Function} props.onBinTypeChange - Callback when bin type changes
 * @param {Function} props.onMaterialChange - Callback when material changes
 * @param {Function} props.onLevelRangeChange - Callback when level range changes
 * @param {Function} props.onSearchChange - Callback when search query changes
 * @returns {JSX.Element} Inventory map filters component
 */
export default function InventoryMapFilters({
  selectedBinType = '',
  selectedMaterial = '',
  selectedLevelRange = '',
  searchQuery = '',
  availableBinTypes = [],
  availableMaterials = [],
  materialColorMap = {},
  onBinTypeChange,
  onMaterialChange,
  onLevelRangeChange,
  onSearchChange,
}) {
  const styles = useStyles();

  return (
    <div className={styles.container}>
      <div className={styles.filterRow}>
        <BnSearchField
          initialValue={searchQuery}
          onChange={onSearchChange}
          className={styles.searchField}
        />
      </div>

      <div className={styles.filterRow}>
        <FormControl className={styles.formControl} size='small'>
          <InputLabel>Bin Type</InputLabel>
          <Select
            value={selectedBinType}
            label='Bin Type'
            onChange={(e) => onBinTypeChange(e.target.value)}
          >
            <MenuItem value=''>All Types</MenuItem>
            {availableBinTypes.map(binType => (
              <MenuItem key={binType} value={binType}>{binType}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className={styles.filterRow}>
        <FormControl className={styles.formControl} size='small'>
          <InputLabel>Materials</InputLabel>
          <Select
            value={selectedMaterial}
            label='Materials'
            onChange={(e) => onMaterialChange(e.target.value)}
          >
            <MenuItem value=''>All Materials</MenuItem>
            {availableMaterials.map(material => (
              <MenuItem key={material} value={material}>
                <Box className={styles.materialItem}>
                  <span
                    className={styles.colorSymbol}
                    style={{ color: materialColorMap[material] || DEFAULT_MATERIAL_COLOR }}
                  >
                    ⬤
                  </span>
                  {material}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className={styles.filterRow}>
        <FormControl className={styles.formControl} size='small'>
          <InputLabel>Levels</InputLabel>
          <Select
            value={selectedLevelRange}
            label='Levels'
            onChange={(e) => onLevelRangeChange(e.target.value)}
          >
            <MenuItem value=''>All Levels</MenuItem>
            <MenuItem value={LevelRanges.ZERO_TO_TWENTY_FIVE}>0-25%</MenuItem>
            <MenuItem value={LevelRanges.TWENTY_FIVE_TO_FIFTY}>25-50%</MenuItem>
            <MenuItem value={LevelRanges.FIFTY_TO_SEVENTY_FIVE}>50-75%</MenuItem>
            <MenuItem value={LevelRanges.SEVENTY_FIVE_TO_HUNDRED}>75-100%</MenuItem>
          </Select>
        </FormControl>
      </div>
    </div>
  );
}
